import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './Alerts.styles';
import AlertCard from './components/AlertCard';
import {
  IconClock, IconClose, IconEllipsis, IconMinus, IconTrash, Skeleton, Tooltip,
} from 'gatorcomponents';
import { SettingsPopover } from '../SettingsPopover';
import { merlinActions } from '@/redux/modules';
import { useDispatch } from 'react-redux';
import NoPendingIssues from './media/no-issues.svg';
import { NON_REMOVABLE_ALERTS } from './Alerts.types';

const Alerts = ({
  isOpen,
  alertsList,
  alertsTypes,
  alertsLoaded = false,
  handleAlertAction,
  getSettingsPopoverProps,
  toggleMinimize,
  closeAlerts,
  locales,
}) => {
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const dispatch = useDispatch();

  const alertsTypesValues = alertsTypes && Object.values(alertsTypes);
  const hasAlertsToLoad = alertsTypesValues && alertsTypesValues.some(alert => !alert.loaded);

  const handleSelectAlerts = (slug) => {
    if (selectedAlerts.includes(slug)) {
      setSelectedAlerts(selectedAlerts.filter(item => item !== slug));
    } else {
      setSelectedAlerts([...selectedAlerts, slug]);
    }
  };

  const canExclude = selectedAlerts.length > 1
  || (selectedAlerts.length === 1 && !selectedAlerts[0].includes(NON_REMOVABLE_ALERTS.DOMAIN_NOT_CONFIGURED));
  const notRemovableAlertsSelectedTitle = alertsList && alertsList.find(alert => alert.type.slug === selectedAlerts[0]);

  const handleRemoveAlerts = useCallback(({ selectedAlertsIds, delay }) => {
    if (!canExclude && !delay) {
      return;
    }

    const nonRemovableAlertsSelected = selectedAlertsIds.filter(alert => Object.values(NON_REMOVABLE_ALERTS).includes(alert));

    const nonRemovableTitle = alertsList.filter((alert) => {
      if (nonRemovableAlertsSelected.includes(alert.type.slug)) {
        return alert.title;
      }
      return false;
    });
    setSelectedAlerts([]);

    const selectedAlerts = [];
    selectedAlertsIds.forEach((alertId) => {
      const selectedAlert = alertsList && alertsList.find(alert => alert.type.slug === alertId);
      selectedAlerts.push({ type: alertId, elements: selectedAlert.elements || [] });
    });

    const removableAlerts = selectedAlerts.filter(alert => !nonRemovableAlertsSelected.includes(alert.type));
    const alerts = delay ? selectedAlerts : removableAlerts;
    const unDoAction = () => {
      dispatch(merlinActions.alerts.removeAlerts({
        delay, alerts, nonRemovableTitle, undo: true,
      }));
    };


    dispatch(merlinActions.alerts.removeAlerts({
      delay, alerts, nonRemovableTitle, unDoAction,
    }));
  }, [alertsList, dispatch, canExclude]);


  const handleToggleSettings = useCallback(() => {
    setShowSettings(prev => !prev);
  }, []);

  const checkForAlertsUpdates = useCallback(() => {
    const allAlerts = alertsTypes && Object.values(alertsTypes);
    if (alertsTypes && isOpen) {
      allAlerts.forEach((alert) => {
        dispatch(merlinActions.alerts.requestAlertPerSlug({ slug: alert.slug }));
      });
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    checkForAlertsUpdates();
  }, [checkForAlertsUpdates, dispatch]);

  const renderToolbar = () => (
    <Styles.Toolbar>
      <Styles.ContentTitle>{locales.alerts.title}</Styles.ContentTitle>
      <Styles.ChatActions>
        {selectedAlerts.length === 0 && (
          <Tooltip content={locales.tooltips.closeAlerts} preferredPosition="bottomLeft">
            <Styles.ToolbarButton onClick={closeAlerts}>
              <IconClose size="md" color="lowDark" />
            </Styles.ToolbarButton>
          </Tooltip>
        )}
        {selectedAlerts.length > 0 && (
          <>
            <Tooltip content={locales.tooltips.delay} preferredPosition="bottomLeft">
              <Styles.ToolbarButton onClick={() => handleRemoveAlerts({ selectedAlertsIds: selectedAlerts, delay: true })}>
                <IconClock size="md" color="lowDark" />
              </Styles.ToolbarButton>
            </Tooltip>

            <Tooltip content={canExclude ? locales.tooltips.delete : locales.tooltips.cantDelete(notRemovableAlertsSelectedTitle.title)} preferredPosition="bottomLeft">
              <Styles.ToolbarButton onClick={() => handleRemoveAlerts({ selectedAlertsIds: selectedAlerts, delay: false })} $disabled={!canExclude}>
                <IconTrash size="md" color={canExclude ? 'lowDark' : 'lowMedium'} />
              </Styles.ToolbarButton>
            </Tooltip>
          </>
        )}

        <Tooltip content={locales.tooltips.minify} preferredPosition="bottomLeft">
          <Styles.ToolbarButton onClick={toggleMinimize}>
            <IconMinus size="sm" color="lowDark" />
          </Styles.ToolbarButton>
        </Tooltip>

        <Tooltip content={locales.tooltips.settings}>
          <Styles.ToolbarButton onClick={handleToggleSettings} data-testid="toggle-settings">
            <IconEllipsis color="lowDark" />
          </Styles.ToolbarButton>
        </Tooltip>
      </Styles.ChatActions>
    </Styles.Toolbar>
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Styles.Container data-testid="alerts-container">
      {renderToolbar()}

      {showSettings && (
        <SettingsPopover
          {...getSettingsPopoverProps()}
          isSideContent
        />
      )}

      {alertsList.length === 0 && alertsLoaded && (
        <Styles.EmptyStateWrapper>
          <Styles.Image src={NoPendingIssues} />
          <Styles.EmptyStateTitle>{locales.alerts.emptyTitle}</Styles.EmptyStateTitle>
          <Styles.EmptyStateDescription>
            {locales.alerts.emptyDescription}
          </Styles.EmptyStateDescription>
        </Styles.EmptyStateWrapper>
      )}

      {alertsList.length > 0 && alertsList.map(item => (
        <React.Fragment key={item.type.slug}>
          <AlertCard
            item={item}
            isChecked={selectedAlerts.includes(item.type.slug)}
            handleSelectAlerts={handleSelectAlerts}
            handleAlertAction={() => handleAlertAction(item)}
          />
        </React.Fragment>
      ))}

      {hasAlertsToLoad && (
        <Styles.SkeletonWrapper>
          <Skeleton width="100%" height="65px" />
        </Styles.SkeletonWrapper>
      )}
    </Styles.Container>
  );
};
export default Alerts;
